import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { CheckCircle2, MessageSquareHeart, X } from "lucide-react"
import { ToastMessage, useToast } from "@stayframe/ui"
import { apiClient, formatApiError, type PageFeedbackAnswer } from "@/lib/api"
import { cn } from "@/lib/utils"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { usePlanContext } from "@/lib/usePlanContext"
import { FeedbackPanel } from "./FeedbackPanel"
import type { PageSlug } from "./questions"

interface FeedbackTriggerProps {
  pageSlug: PageSlug
  className?: string
}

type FeedbackView = "hidden" | "idle" | "expanded" | "thankYou" | "dismissed"
type ThankYouMode = "score" | "full"

const SHOW_DELAY_MS = 5000
const THANK_YOU_MS = 3000
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000
const SESSION_SHOWN_KEY = "pageFeedback-trigger-shown"
const LEGACY_SESSION_SHOWN_KEY = "pageFeedbackTriggerShown"

function getVisitsKey(pageSlug: PageSlug) {
  return `pageFeedback-${pageSlug}-visits`
}

function getLastSubmittedKey(pageSlug: PageSlug) {
  return `pageFeedback-${pageSlug}-lastSubmitted`
}

function readStorageValue(storage: Storage | null, key: string): string {
  try {
    return storage?.getItem(key) || ""
  } catch {
    return ""
  }
}

function writeStorageValue(storage: Storage | null, key: string, value: string) {
  try {
    storage?.setItem(key, value)
  } catch {}
}

function getStoredNumber(storage: Storage | null, key: string): number {
  const raw = Number(readStorageValue(storage, key))
  return Number.isFinite(raw) ? raw : 0
}

export function FeedbackTrigger({ pageSlug, className }: FeedbackTriggerProps) {
  const { propertyId } = usePropertyContext()
  const { degraded, isTrialing, status, isLoading: planLoading } = usePlanContext()
  const { toast } = useToast()

  const [visitCount, setVisitCount] = useState(0)
  const [hasRecentSubmission, setHasRecentSubmission] = useState(false)
  const [sessionShown, setSessionShown] = useState(false)
  const [view, setView] = useState<FeedbackView>("hidden")
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [thankYouMode, setThankYouMode] = useState<ThankYouMode>("full")

  const partialSavePromiseRef = useRef<Promise<boolean> | null>(null)
  const partialSavedScoreRef = useRef<number | null>(null)
  const completedRef = useRef(false)

  useEffect(() => {
    const nextVisits = getStoredNumber(window.localStorage, getVisitsKey(pageSlug)) + 1
    writeStorageValue(window.localStorage, getVisitsKey(pageSlug), String(nextVisits))
    setVisitCount(nextVisits)

    const lastSubmittedAt = getStoredNumber(window.localStorage, getLastSubmittedKey(pageSlug))
    setHasRecentSubmission(Boolean(lastSubmittedAt) && Date.now() - lastSubmittedAt < NINETY_DAYS_MS)

    const alreadyShown = readStorageValue(window.sessionStorage, SESSION_SHOWN_KEY) === "1"
      || readStorageValue(window.sessionStorage, LEGACY_SESSION_SHOWN_KEY) === "1"
    setSessionShown(alreadyShown)
  }, [pageSlug])

  const planEligible = useMemo(
    () => !planLoading && !degraded && (isTrialing || status === "active" || status === "past_due"),
    [degraded, isTrialing, planLoading, status],
  )

  const canShow = Boolean(propertyId)
    && planEligible
    && visitCount >= 3
    && !hasRecentSubmission
    && !sessionShown

  const markSessionShown = useCallback(() => {
    writeStorageValue(window.sessionStorage, SESSION_SHOWN_KEY, "1")
    writeStorageValue(window.sessionStorage, LEGACY_SESSION_SHOWN_KEY, "1")
    setSessionShown(true)
  }, [])

  const markSubmitted = useCallback(() => {
    const now = Date.now()
    writeStorageValue(window.localStorage, getLastSubmittedKey(pageSlug), String(now))
    setHasRecentSubmission(true)
  }, [pageSlug])

  function showApiError(result: Record<string, unknown>, fallback: string) {
    const error = formatApiError(result)
    toast(<ToastMessage message={error.message || fallback} requestId={error.requestId} />, "error")
  }

  const saveScoreOnly = useCallback(async (score: number): Promise<boolean> => {
    if (!propertyId || completedRef.current) return true
    if (partialSavedScoreRef.current === score) return true
    if (partialSavePromiseRef.current) return partialSavePromiseRef.current

    partialSavePromiseRef.current = (async () => {
      const response = await apiClient.submitPageFeedback(propertyId, {
        page_slug: pageSlug,
        score,
        answers: [],
      })
      if (response.ok) {
        partialSavedScoreRef.current = score
        markSubmitted()
        return true
      }
      return false
    })()

    try {
      return await partialSavePromiseRef.current
    } finally {
      partialSavePromiseRef.current = null
    }
  }, [markSubmitted, pageSlug, propertyId])

  useEffect(() => {
    if (!canShow || view !== "hidden") return
    const timer = window.setTimeout(() => {
      markSessionShown()
      setView("idle")
    }, SHOW_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [canShow, markSessionShown, view])

  useEffect(() => {
    if (view !== "thankYou") return
    const timer = window.setTimeout(() => {
      setView("dismissed")
    }, THANK_YOU_MS)
    return () => window.clearTimeout(timer)
  }, [view])

  const handleExpand = useCallback(() => {
    markSessionShown()
    setView("expanded")
  }, [markSessionShown])

  const handleDismiss = useCallback(() => {
    markSessionShown()
    setView("dismissed")
  }, [markSessionShown])

  const handleScoreSelect = useCallback((score: number) => {
    setSelectedScore(score)
    void saveScoreOnly(score)
  }, [saveScoreOnly])

  const handlePanelClose = useCallback(async () => {
    if (selectedScore == null || completedRef.current) {
      setView("dismissed")
      return
    }

    const saved = await saveScoreOnly(selectedScore)
    if (saved) {
      setThankYouMode("score")
      setView("thankYou")
      return
    }

    setView("dismissed")
    toast("We couldn't save that score. Please try again later.", "error")
  }, [saveScoreOnly, selectedScore, toast])

  const handleSubmit = useCallback(async (score: number, answers: PageFeedbackAnswer[]) => {
    if (!propertyId) return
    setSubmitting(true)

    if (partialSavePromiseRef.current) {
      await partialSavePromiseRef.current
    }

    const response = await apiClient.submitPageFeedback(propertyId, {
      page_slug: pageSlug,
      score,
      answers,
    })

    if (response.ok) {
      completedRef.current = true
      partialSavedScoreRef.current = score
      markSubmitted()
      setThankYouMode("full")
      setView("thankYou")
      setSubmitting(false)
      return
    }

    if (partialSavedScoreRef.current === score) {
      completedRef.current = true
      setThankYouMode("score")
      setView("thankYou")
      toast("Your score was saved, but the extra details did not go through.", "info")
      setSubmitting(false)
      return
    }

    showApiError(response as unknown as Record<string, unknown>, "Failed to submit feedback.")
    setSubmitting(false)
  }, [markSubmitted, pageSlug, propertyId, toast])

  if (view === "hidden" || view === "dismissed") return null

  return (
    <div className={cn("fixed bottom-20 right-6 z-50", className)}>
      {view === "idle" ? (
        <div className="animate-in slide-in-from-bottom-2 fade-in duration-300 flex items-center gap-2 rounded-full border border-border/60 bg-card/95 px-4 py-2.5 shadow-lg backdrop-blur-sm transition-all duration-200 hover:border-border hover:shadow-xl">
          <button
            type="button"
            onClick={handleExpand}
            className="flex items-center gap-2 text-left transition-transform duration-200 hover:scale-[1.02]"
          >
            <MessageSquareHeart className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">How useful is this page?</span>
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss feedback prompt"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}

      {view === "expanded" ? (
        <div className="animate-in slide-in-from-bottom-2 fade-in duration-300">
          <FeedbackPanel
            pageSlug={pageSlug}
            onScoreSelect={handleScoreSelect}
            onSubmit={handleSubmit}
            onClose={() => {
              void handlePanelClose()
            }}
            isSubmitting={submitting}
          />
        </div>
      ) : null}

      {view === "thankYou" ? (
        <div className="animate-in fade-in duration-200 w-[min(360px,calc(100vw-2rem))] rounded-xl border border-border bg-card p-5 shadow-xl">
          <CheckCircle2 className="mx-auto h-6 w-6 text-success" />
          <h3 className="mt-3 text-center text-sm font-semibold text-foreground">Thanks for sharing!</h3>
          <p className="mt-1 text-center text-xs text-muted-foreground">
            {thankYouMode === "full"
              ? "Your feedback helps us improve this page for all hosts."
              : "Your score is saved and helps us prioritize improvements."}
          </p>
        </div>
      ) : null}
    </div>
  )
}
